import { useSelector } from "@legendapp/state/react";
import { HOTEL_DETAILS_ACTIVE_TAB } from "@website/accommodation-ui/constant/keys";
import { HotelCardInfoOptions } from "@website/accommodation-ui/hotel-card-new/hotel-card-info/hotel-card-info-options";
import { HotelCardInfoPropertiesType } from "@website/accommodation-ui/hotel-card-new/hotel-card-info/hotel-card-info.types";
import { HotelCardRating } from "@website/accommodation-ui/hotel-card-new/hotel-card-info/hotel-card-rating";
import {
  HotelCardLink,
  HotelCardLinkProperties
} from "@website/accommodation-ui/hotel-card-new/hotel-card-link";
import { HotelCardStars } from "@website/accommodation-ui/hotel-card-new/hotel-card-stars";
import { HotelSnappPayBadge } from "@website/accommodation-ui/hotel-snapp-pay";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { getTracking } from "@website/store/tracking";
import { LazyImage } from "@website/ui/lazy-image";
import { MiniChips } from "@website/ui/mini-chips";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import snappProBadge from "@website/widget/header/assets/pro_badge.svg";
import omit from "lodash-es/omit";
import { FC, MouseEvent, memo, useCallback } from "react";

const Component: FC<HotelCardInfoPropertiesType> = (properties) => {
  const router = useCompatRouter();
  const {
    hotelTitle,
    stars,
    rating,
    reviewCount,
    address,
    roomTitle,
    roomCount,
    regularBadges,
    advertisementBadge,
    size = "responsive",
    isFomo,
    snappPayData,
    loading,
    pricing,
    isSuggestedCard,
    facilities,
    id,
    showMap,
    location,
    propertyId,
    reservationInfo,
    isInstant,
    linkProperties,
    isAvailable,
    hasSnappProDiscount,
    transportation
  } = properties;
  const { date_from, date_to } = router.query;
  const { isMobile } = useViewPort();
  const TitleComponent = size === "small" ? "span" : "h3";
  const appName = process.env.NEXT_PUBLIC_APP_NAME;
  const isVillaAllowed = appName === "villa" && size !== "small";
  const experiment = useSelector(getTracking().experiment);
  const isHotel = ["hotel-domestic", "hotel-international", "tour"].includes(
    appName ?? ""
  );
  const isInternational =
    appName === "hotel-international" || properties.isInternational;
  const isHomePage = appName === "homepage" || appName === "website";

  const hasDate = Boolean(date_from && date_to);
  const hasPassenger =
    Boolean(reservationInfo?.bedCount) && Number(reservationInfo?.bedCount) > 0;

  const onHotelDetailClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      router.replace(
        router?.pathname,
        {
          ...router?.query,
          [HOTEL_DETAILS_ACTIVE_TAB]: "features",
          selectedHotelId: id
        },
        { shallow: true, scroll: false }
      );
    },
    [id, router]
  );

  return (
    <div
      className={`flex min-w-0 shrink grow flex-col ${
        size === "xSmall"
          ? ""
          : size === "small"
            ? "gap-2"
            : "gap-3 pt-3 md:px-3 lg:pt-0"
      } `}
    >
      <div
        className={`flex flex-col ${size === "xSmall" ? "gap-0" : "gap-0.5"}`}
      >
        <div className="flex flex-row items-center gap-1">
          {advertisementBadge?.text && (
            <MiniChips
              color="ads"
              size="small"
              text={advertisementBadge?.text}
              variant="contained"
            />
          )}
          <HotelCardLink
            {...(linkProperties as HotelCardLinkProperties)}
            className="min-w-0 flex-1 truncate"
          >
            <TitleComponent
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className={`hotel-card-title text-on-surface-high-emphasis flex flex-row gap-1 truncate font-bold ${
                size === "xSmall"
                  ? "text-xs"
                  : size === "small"
                    ? "text-subtitle-1"
                    : ""
              }`}
              data-testid="hotel_card_title"
              dir={appName === "hotel-international" ? "ltr" : "rtl"}
              itemProp="name"
            >
              {hasSnappProDiscount ? (
                <LazyImage
                  className="shrink-0"
                  src={snappProBadge.src}
                  alt="Snapp Pro"
                  loading="eager"
                  fetchPriority="high"
                  width={size === "xSmall" ? 16 : 24}
                  height={size === "xSmall" ? 16 : 24}
                />
              ) : null}
              <span className="block min-w-0 truncate">
                {numberConvert(hotelTitle, {
                  locale: isInternational ? "en" : router.locale || "fa"
                })}
              </span>
            </TitleComponent>
          </HotelCardLink>
        </div>

        <div
          className={`${
            isSuggestedCard
              ? "mt-1 flex flex-col items-start justify-between"
              : "hidden"
          }`}
        >
          <HotelCardStars size={size} starSize="small" stars={stars} />

          {size === "xSmall" && rating && Boolean(rating > 0) ? (
            <HotelCardRating
              id={id}
              hasIcon={false}
              isSuggestedCard={false}
              propertyId={propertyId}
              rating={rating}
              reviewCount={reviewCount}
              size={size}
            />
          ) : null}
          {stars === 0 && size === "xSmall" && (
            <div className="!h-[14px]"></div>
          )}
          {rating === 0 && size === "xSmall" && (
            <div className="!h-[16px]"></div>
          )}
        </div>
      </div>
      {router.pathname.includes("map") && (
        <HotelCardStars size={size} starSize="large" stars={stars} />
      )}
      <div
        className={`md:gap-3 ${
          size === "small"
            ? `${
                appName === "villa" || isHomePage
                  ? "!justify-start md:!gap-2"
                  : "!justify-between"
              } pb-1 md:!gap-1`
            : ""
        } flex flex-row ${isMobile ? "!justify-between" : "justify-start"}`}
      >
        {size !== "xSmall" && !isSuggestedCard && (
          <HotelCardRating
            hasIcon={false}
            id={id}
            propertyId={propertyId}
            rating={rating}
            reviewCount={reviewCount}
            size={size}
          />
        )}
        {!isSuggestedCard && (
          <div className="flex flex-row items-center justify-between">
            {size === "small" &&
            (appName === "villa" || isHomePage) &&
            address ? (
              <div
                className={`${
                  location?.lat &&
                  location?.lng &&
                  !router?.pathname?.includes("map")
                    ? "text-secondary"
                    : "text-on-surface-medium-emphasis"
                } text-caption flex flex-row items-center gap-1`}
              >
                <MaterialIcon name="location_on" variant="outlined" />
                <span
                  className={`max-w-[110px] truncate ${
                    location?.lat &&
                    location?.lng &&
                    !router?.pathname?.includes("map")
                      ? "cursor-pointer"
                      : ""
                  }`}
                  onClick={
                    location?.lat &&
                    location?.lng &&
                    !router?.pathname?.includes("map")
                      ? (event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          return router?.push(
                            `${isHomePage ? "/villa" : ""}/map`,
                            {
                              ...omit(router?.query, [
                                "cityTitle",
                                "locationType",
                                "extra_capacity",
                                "modal"
                              ]),
                              selectedHotelId: id,
                              coordinate: `${location?.lat},${location?.lng},,15`
                            },

                            { shallow: false, scroll: false }
                          );
                        }
                      : undefined
                  }
                >
                  {address}
                </span>
              </div>
            ) : router.pathname.includes("map") ? null : (
              <HotelCardStars size={size} starSize="large" stars={stars} />
            )}
            {size === "xSmall" && (
              <HotelCardRating
                id={id}
                propertyId={propertyId}
                rating={rating}
                reviewCount={reviewCount}
                size={size}
              />
            )}
          </div>
        )}
      </div>
      {roomTitle && size === "small" && appName === "villa" && (
        <div
          className={`${
            size === "small" ? "w-[200px]" : "w-[120px]"
          } text-on-surface-medium-emphasis flex items-center justify-start gap-1 md:w-[200px]`}
        >
          {size === "small" && <MaterialIcon name="person" />}
          <span
            className={`text-caption ${size === "small" ? "" : "truncate"}`}
            data-testid="hotel_card_room_title"
          >
            {numberConvert(roomTitle, {
              numberOnly: false,
              locale: router.locale
            })}
          </span>
        </div>
      )}
      {appName === "villa" &&
        hasDate &&
        hasPassenger &&
        pricing?.price &&
        experiment["experiment_villa_pricing"] &&
        pricing?.basePrice &&
        size === "small" && (
          <div className="text-on-surface-medium-emphasis flex items-center justify-start gap-1">
            <MaterialIcon name="payments" variant="outlined" />
            <div className="text-caption whitespace-nowrap pe-1">
              شروع قیمت از
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-subtitle-2">
                {numberConvert(numberSeparator(pricing?.basePrice), {
                  locale: router.locale || "fa"
                })}
                <span className="text-caption px-1">تومان</span>
              </div>
            </div>
          </div>
        )}

      {appName === "villa" &&
        hasDate &&
        hasPassenger &&
        pricing?.price &&
        experiment["experiment_villa_pricing"] !== true &&
        size === "small" && (
          <div className="text-on-surface-medium-emphasis flex items-center justify-start gap-1">
            {size === "small" && (
              <MaterialIcon name="payments" variant="outlined" />
            )}
            <div className="text-caption whitespace-nowrap">{`${numberConvert(
              reservationInfo?.bedCount === 0 ? 1 : reservationInfo?.bedCount,
              { locale: router.locale || "fa" }
            )} نفر و ${numberConvert(reservationInfo?.nightCount, {
              locale: router?.locale || "fa"
            })} شب`}</div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-subtitle-2">
                {numberConvert(numberSeparator(pricing?.price), {
                  locale: router.locale || "fa"
                })}
                <span className="text-caption px-1">تومان</span>
              </div>
            </div>
          </div>
        )}
      {appName === "tour" && transportation ? (
        <div className="flex flex-row items-center gap-1">
          {transportation.icons.map((icon) => (
            <MaterialIcon
              key={`icon-${icon}`}
              className={`text-xl ${icon === "flight" ? "rotate-45" : ""}`}
              name={icon}
            />
          ))}

          <div className="text-on-surface-high-emphasis text-sm">
            {transportation.text}
          </div>
        </div>
      ) : null}
      <meta content={address} itemProp="address" />
      {size !== "xSmall" && (
        <HotelCardInfoOptions
          className={
            size === "small"
              ? "flex flex-col gap-2"
              : "flex flex-col gap-2 lg:gap-1"
          }
          address={address}
          facilities={facilities}
          isFomo={isFomo}
          isSuggestedCard={isSuggestedCard}
          regularBadges={regularBadges}
          roomCount={roomCount}
          roomTitle={roomTitle}
          size={size}
        />
      )}
      {size !== "small" && size !== "xSmall" && (
        <div className="mt-auto hidden md:block">
          {isAvailable && (
            <HotelSnappPayBadge
              {...snappPayData}
              loading={loading}
              page="cityPage"
              price={pricing?.priceOff || pricing?.price}
            />
          )}
        </div>
      )}
      <div
        className={`${
          size === "small" && !isMobile ? "" : "mb-2"
        } flex flex-row items-center ${
          appName === "villa" || isHomePage
            ? "justify-start"
            : "justify-between"
        } gap-1`}
      >
        {!router.pathname.includes("map") &&
        !isSuggestedCard &&
        (showMap || address) &&
        (isVillaAllowed || isHotel) ? (
          <div className="flex min-w-0 items-center justify-start gap-1">
            <MaterialIcon
              className={`shrink-0 ${
                showMap ? "text-secondary" : "text-on-surface-high-emphasis"
              }`}
              name="location_on"
              variant="outlined"
            />
            <span
              className={`md:max-w-[255px] ${
                showMap
                  ? "text-secondary cursor-pointer"
                  : "text-on-surface-high-emphasis"
              } text-caption truncate`}
              dir={isInternational && appName !== "tour" ? "ltr" : ""}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (!showMap) return;
                if (
                  appName === "hotel-domestic" &&
                  location?.lat &&
                  location?.lng
                )
                  return router?.[
                    router.pathname.includes("map") ? "replace" : "push"
                  ](
                    `${isHomePage ? "/villa" : ""}/map`,
                    {
                      ...omit(router?.query, [
                        "hotelTitle",
                        "cityTitle",
                        "locationType",
                        "extra_capacity",
                        "modal"
                      ]),
                      selectedHotelId: id,
                      coordinate: `${location?.lat},${location?.lng},11,33`
                    },
                    { shallow: false, scroll: false }
                  );
                return router.replace(
                  router?.pathname,
                  {
                    ...router?.query,
                    modal: "map",
                    selectedHotelId: id
                  },
                  { shallow: true, scroll: false }
                );
              }}
            >
              {address ?? "مشاهده روی نقشه"}
            </span>
          </div>
        ) : (
          <div />
        )}
        {appName === "tour" ? (
          <button
            className="text-secondary text-caption min-w-max cursor-pointer max-md:text-start"
            type="button"
            onClick={onHotelDetailClick}
          >
            جزییات هتل
          </button>
        ) : null}
        <div
          className={`flex flex-wrap justify-start gap-3 ${
            !regularBadges?.length && !isInstant ? "hidden" : ""
          } ${
            (process.env.NEXT_PUBLIC_APP_NAME === "villa" || isHomePage) &&
            isMobile &&
            "mb-2"
          }`}
        >
          {isInstant && (
            <MiniChips
              StartIcon={<MaterialIcon name="bolt" />}
              className={`!bg-warning !text-on-surface-high-emphasis ${
                isInstant ? "" : "!hidden"
              }`}
              text="رزرو آنی"
              variant="contained"
            />
          )}
          {/* {Boolean(categories?.length) &&
          categories?.map((category, index) => (
            <MiniChips
              key={`${category.id}-${index}`}
              text={category.name}
              variant="accent"
            />
          ))} */}
          {process.env.NEXT_PUBLIC_APP_NAME === "villa" &&
            Boolean(regularBadges?.length) &&
            regularBadges?.map((badge, index) => (
              <MiniChips
                key={`${badge.text}-${index}`}
                text={numberConvert(badge.text, {
                  locale: router.locale || "fa"
                })}
                variant="accent"
              />
            ))}
        </div>
        {/* {(isInstant || appName === "villa") && (
          <div
            className={`flex flex-wrap justify-start gap-3 ${
              appName === "villa" && isMobile && "mb-2"
            }`}
          >
            {isInstant && (
              <MiniChips
                StartIcon={<MaterialIcon name="bolt" />}
                className="!bg-warning !text-on-surface-high-emphasis"
                text="رزرو آنی"
                variant="contained"
              />
            )}
            {appName === "villa" &&
              Boolean(regularBadges?.length) &&
              regularBadges?.map((badge, index) => (
                <MiniChips
                  key={`${badge.text}-${index}`}
                  text={numberConvert(badge.text, {
                    locale: router.locale || "fa"
                  })}
                  variant="accent"
                />
              ))}
          </div>
        )} */}
      </div>
    </div>
  );
};

export const HotelCardInfo = memo(Component, deepCompare);
